/* eslint-disable max-len */
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Grid, IconButton, Box, FormControl, FormLabel, RadioGroup, FormControlLabel, Radio, Typography } from '@mui/material';
import { endOfDay } from 'date-fns';

import {
    LocalShipping,
    Construction,
    CardMembership,
    Forest,
    ExpandLess,
    ExpandMore,
    Add,
    Remove
} from '@mui/icons-material';

import {
    AutocompleteField,
    NumberField,
    TextField,
    DateTimeField,
    BoolField
} from '../../CommonComponents/Form';
import FormContext from '../../CommonComponents/Form/FormContext';

import {
    hasStandardProperty,
    toggleStandardProperty,
    removeCustomLicenceCertifiacate,
    changeCustomLicenceCertificate,
    isCustomCertAlreadyInList,
    isCustomCertInPresets
} from '../../../utils/metadata';

import { SectionDivider } from '../../CommonComponents';

import { fetchAllGroups, selectMyGroups } from '../../../redux/actions/groups';
import { fetchSettableMetadataForOrder, selectSettableMetadataForOrder } from '../../../redux/actions/metadata';
import { selectUserProfile } from '../../../redux/actions/users';
import { useDispatch, useSelector } from '../../../redux/hooks';
import { searchCarriers } from '../../../api-public/company';
import OrderDate from './OrderDate';
import OrderLocation from './OrderLocation';
import { validateOrderVolumeAndLoadingMeters } from '../../CommonComponents/Form/validations/validations';

import { METADATA_TYPES } from '../../../types/MetadataType';

type PropsType = {
    entity: { [key: string]: any },
    onChange: (e: { [key: string]: any }) => void,
    skipRequiredValidation?: boolean
};

export default function OrderFormFields(props: PropsType) {
    const { id } = useParams();
    const {
        onChange,
        entity,
        skipRequiredValidation,
    } = props;

    const dispatch = useDispatch();
    const groups = useSelector(selectMyGroups());
    const userProfile = useSelector(selectUserProfile());
    const settableMetadata = useSelector(selectSettableMetadataForOrder());
    const ownTenantIdRef = React.useRef(userProfile?.tenantId);
    ownTenantIdRef.current = userProfile?.tenantId;

    const fetchGroups = React.useCallback(() => {
        dispatch(fetchAllGroups());
    }, [dispatch]);

    const volumeAndLoadingMetersValidation = useMemo(() => [validateOrderVolumeAndLoadingMeters({
        volume: entity.volume,
        loadingMeters: entity.loadingMeters
    })], [entity.volume, entity.loadingMeters]);

    const formContext = React.useContext(FormContext);
    const isSubmitting = formContext?.isSubmitting;

    const fetchCarriers = React.useCallback(async (query?: string) => {
        const results = await searchCarriers(query);
        const ownTenantId = ownTenantIdRef.current;
        return (results || []).filter((c: { id: number }) => c.id !== ownTenantId);
    }, []);

    const [initialEntity, setInitialEntity] = React.useState<{ [key: string]: any } | undefined>(undefined);

    const [
        customLicencesCertificates,
        setCustomLicencesCertificates
    ] = React.useState<Array<{ id: string; value: string }>>([]);

    const licenseCount = (entity.licencesCertificates?.filter((lic: string) => settableMetadata.licencesCertificates
        .includes(lic)).length || 0) + customLicencesCertificates.filter((clic) => clic.value.trim()).length;

    const [collapsed, setCollapsed] = React.useState<{ [key: string]: boolean }>({
        vehicleTypes: false,
        equipment: false,
        sustainability: false,
        licencesCertificates: false
    });

    const toggleCollapsed = (key: string) => {
        setCollapsed((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    const addCustomLicenceCertificate: () => void = () => {
        setCustomLicencesCertificates([
            ...customLicencesCertificates,
            { id: `custom-cert-${Date.now().toString()}`, value: '' }
        ]);
    };

    React.useEffect(() => {
        dispatch(fetchSettableMetadataForOrder());
    }, [dispatch]);

    React.useEffect(() => {
        if (!initialEntity) return;
        const mappedCustomLicCert = initialEntity.customLicencesCertificates
            .map((cert: string, idx: number) => (
                { id: `init-cert-${idx}`, value: cert }
            ));
        setCustomLicencesCertificates(mappedCustomLicCert);
    }, [initialEntity]);

    React.useEffect(() => {
        if (entity !== undefined) {
            setInitialEntity(entity);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    return (
        <>
            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Upphämtningsdatum" marginTop={0} />
                    <OrderDate
                        partialKey="pickup"
                        partialName="upphämtnings"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Leveransdatum" marginTop={0} />
                    <OrderDate
                        partialKey="delivery"
                        partialName="leverans"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                        firstMinDate={entity.firstPickupDate ? entity.firstPickupDate : undefined}
                        lastMinDate={entity.lastPickupDate ? entity.lastPickupDate : undefined}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Upphämtningsplats" />
                    <OrderLocation
                        partialKey="pickup"
                        partialName="upphämtnings"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Leveranssplats" />
                    <OrderLocation
                        partialKey="delivery"
                        partialName="leverans"
                        entity={entity}
                        onChange={onChange}
                        skipRequiredValidation={skipRequiredValidation}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={{ xs: 2, md: 4 }}>
                <Grid item xs={12} md={6}>
                    <SectionDivider label="Gods" />

                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="grossWeight"
                                label="Totalvikt [kg]"
                                value={entity.grossWeight}
                                onChange={onChange}
                                minValue={50}
                                required={!skipRequiredValidation}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="chargeableWeight"
                                label="FDR-vikt [kg]"
                                value={entity.chargeableWeight}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="loadingMeters"
                                label="Flakmeter"
                                value={entity.loadingMeters}
                                onChange={onChange}
                                minValue={0}
                                extraValidations={volumeAndLoadingMetersValidation}

                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="volume"
                                label="Volym [m³]"
                                value={entity.volume}
                                onChange={onChange}
                                minValue={0}
                                extraValidations={volumeAndLoadingMetersValidation}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="palletPlaces"
                                label="Pallplatser"
                                value={entity.palletPlaces}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="pallets"
                                label="Pallar"
                                value={entity.pallets}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        {/* <Grid item xs={12} lg={6}>
                            <NumberField
                                name="pieces"
                                label="Stycken"
                                value={entity.pieces}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid> */}
                    </Grid>
                    <SectionDivider label="Övriga krav" marginBottom={-3} />
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <SectionDivider
                                label={(
                                    <>
                                        {`Fordonstyper (${entity.vehicleTypes?.length || 0}) `}
                                        {collapsed.vehicleTypes ? <ExpandMore /> : <ExpandLess />}
                                    </>
                                )}
                                icon={<LocalShipping fontSize="small" />}
                                color="inherit"
                                onClick={() => toggleCollapsed(METADATA_TYPES.VEHICLE_TYPES)}
                                marginBottom={1}
                                marginTop={3}
                            />
                            {!collapsed.vehicleTypes && (
                                <Grid container spacing={1} sx={{ width: '100%' }}>
                                    {settableMetadata.vehicleTypes.map((veh) => (
                                        <Grid item xs={12} sm={6} xl={4} sx={{ minWidth: 0 }} key={veh}>
                                            <BoolField
                                                name={veh}
                                                label={veh}
                                                value={hasStandardProperty({
                                                    array: entity.vehicleTypes,
                                                    value: veh
                                                })}
                                                onChange={() => toggleStandardProperty({
                                                    key: METADATA_TYPES.VEHICLE_TYPES,
                                                    array: entity.vehicleTypes,
                                                    value: veh,
                                                    onChange,
                                                    entity
                                                })}
                                                sx={{ py: 0.25 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <SectionDivider
                                label={(
                                    <>
                                        {`Utrustning (${entity.equipment?.length || 0}) `}
                                        {collapsed.equipment ? <ExpandMore /> : <ExpandLess />}
                                    </>
                                )}
                                icon={<Construction fontSize="small" />}
                                color="inherit"
                                onClick={() => toggleCollapsed(METADATA_TYPES.EQUIPMENT)}
                                marginBottom={1}
                                marginTop={3}
                            />
                            {!collapsed.equipment && (
                                <Grid container spacing={1} sx={{ width: '100%' }}>
                                    {settableMetadata.equipment.map((eq) => (
                                        <Grid item xs={12} sm={6} xl={4} sx={{ minWidth: 0 }} key={eq}>
                                            <BoolField
                                                name={eq}
                                                label={eq}
                                                value={hasStandardProperty({
                                                    array: entity.equipment,
                                                    value: eq
                                                })}
                                                onChange={() => toggleStandardProperty({
                                                    key: METADATA_TYPES.EQUIPMENT,
                                                    array: entity.equipment,
                                                    value: eq,
                                                    onChange,
                                                    entity
                                                })}
                                                sx={{ py: 0.25 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <SectionDivider
                                label={(
                                    <>
                                        {`Hållbarhet (${entity.sustainability?.length || 0}) `}
                                        {collapsed.sustainability ? <ExpandMore /> : <ExpandLess />}
                                    </>
                                )}
                                icon={<Forest fontSize="small" />}
                                color="inherit"
                                onClick={() => toggleCollapsed(METADATA_TYPES.SUSTAINABILITY)}
                                marginBottom={1}
                                marginTop={3}
                            />
                            {!collapsed.sustainability && (
                                <Grid container spacing={1} sx={{ width: '100%' }}>
                                    {settableMetadata.sustainability.map((sus) => (
                                        <Grid item xs={12} sm={6} xl={4} sx={{ minWidth: 0 }} key={sus}>
                                            <BoolField
                                                name={sus}
                                                label={sus}
                                                value={hasStandardProperty({
                                                    array: entity.sustainability,
                                                    value: sus
                                                })}
                                                onChange={() => toggleStandardProperty({
                                                    key: METADATA_TYPES.SUSTAINABILITY,
                                                    array: entity.sustainability,
                                                    value: sus,
                                                    onChange,
                                                    entity
                                                })}
                                                sx={{ py: 0.25 }}
                                            />
                                        </Grid>
                                    ))}
                                </Grid>
                            )}
                        </Grid>
                        <Grid item xs={12}>
                            <SectionDivider
                                label={(
                                    <>
                                        {`Licenser & Certifikat (${licenseCount || 0}) `}
                                        {collapsed.licencesCertificates ? <ExpandMore /> : <ExpandLess />}
                                    </>
                                )}
                                icon={<CardMembership fontSize="small" />}
                                color="inherit"
                                onClick={() => toggleCollapsed(METADATA_TYPES.LICENCES_CERTIFICATES)}
                                marginBottom={1}
                                marginTop={3}
                            />
                            {!collapsed.licencesCertificates && (
                                <>
                                    <Grid container spacing={1} sx={{ width: '100%' }}>
                                        {settableMetadata.licencesCertificates.map((lic) => (
                                            <Grid item xs={12} sm={6} xl={4} sx={{ minWidth: 0 }} key={lic}>
                                                <BoolField
                                                    name={lic}
                                                    label={lic}
                                                    value={hasStandardProperty({
                                                        array: entity.licencesCertificates,
                                                        value: lic
                                                    })}
                                                    onChange={() => toggleStandardProperty({
                                                        key: METADATA_TYPES.LICENCES_CERTIFICATES,
                                                        array: entity.licencesCertificates,
                                                        value: lic,
                                                        onChange,
                                                        entity
                                                    })}
                                                    sx={{ py: 0.25 }}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Grid container spacing={1} sx={{ mt: 1 }}>
                                        {customLicencesCertificates.map((cert) => (
                                            <Grid item xs={12} key={cert.id} container spacing={1} alignItems="center">
                                                <Grid item xs>
                                                    <TextField
                                                            name={`custom-cert-${cert.id}`}
                                                            label="Egen licens/certifikat"
                                                            value={cert.value}
                                                            onChange={(e: React.ChangeEvent<HTMLInputElement> | { [key: string]: string }) => {
                                                                const newValue = (e as React.ChangeEvent<HTMLInputElement>).target ? (e as React.ChangeEvent<HTMLInputElement>).target.value : ((e as { [key: string]: string })[`custom-cert-${cert.id}`] ?? '');
                                                                changeCustomLicenceCertificate({
                                                                    certId: cert.id,
                                                                    newValue,
                                                                    customLicencesCertificates,
                                                                    setCustomLicencesCertificates,
                                                                    entity,
                                                                    onChange
                                                                });
                                                            }}
                                                            required
                                                            customValidations={
                                                                [
                                                                    {
                                                                        isValid: !isCustomCertAlreadyInList({
                                                                            certId: cert.id,
                                                                            value: cert.value,
                                                                            certs: customLicencesCertificates
                                                                        }),
                                                                        errorText: 'Detta certifikat finns redan'
                                                                    },
                                                                    {
                                                                        isValid: !isCustomCertInPresets({
                                                                            value: cert.value,
                                                                            presets: settableMetadata.licencesCertificates

                                                                        }),
                                                                        errorText: 'Detta certifikat finns som förinställt alternativ.'
                                                                    }
                                                                ]
                                                            }
                                                            disabled={cert.id.startsWith('init-')}
                                                        />
                                                    </Grid>
                                                    <Grid item>
                                                        <IconButton
                                                            onClick={() => removeCustomLicenceCertifiacate({
                                                                certId: cert.id,
                                                                customLicencesCertificates,
                                                                setCustomLicencesCertificates,
                                                                entity,
                                                                onChange
                                                            })}
                                                            color="error"
                                                            size="small"
                                                        >
                                                            <Remove />
                                                        </IconButton>
                                                    </Grid>
                                                </Grid>
                                        ))}
                                        <Grid item xs={12}>
                                            <IconButton
                                                onClick={() => addCustomLicenceCertificate()}
                                                color="primary"
                                                size="small"
                                            >
                                                <Add />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                </>
                            )}
                        </Grid>
                    </Grid>
                </Grid>

                <Grid item xs={12} md={6}>
                    <SectionDivider label="Publicering" />
                    <Grid container spacing={2}>
                        <Grid item xs={12} lg={6}>
                            <BoolField
                                name="showTenantName"
                                label="Visa ert företagsnamn"
                                value={entity.showTenantName}
                                onChange={onChange}
                                alignItems="center"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl
                                component="fieldset"
                                variant="standard"
                                disabled={isSubmitting}
                                sx={{ width: '100%' }}
                            >
                                <FormLabel
                                    component="legend"
                                    sx={{
                                        mb: 1,
                                        color: 'text.primary',
                                        typography: 'body2',
                                        fontWeight: 600
                                    }}
                                >
                                    Välj hur uppdraget ska publiceras
                                </FormLabel>
                                <RadioGroup
                                    name="publishAudience"
                                    value={entity.isPublic ? 'public' : 'channels'}
                                    onChange={(event) => {
                                        if (event.target.value === 'public') {
                                            onChange({
                                                isPublic: true,
                                                groups: [],
                                                recipients: []
                                            });
                                            return;
                                        }
                                        onChange({ isPublic: false });
                                    }}
                                >
                                    <FormControlLabel
                                        value="public"
                                        control={<Radio sx={{ mt: 0.25 }} />}
                                        sx={{ alignItems: 'flex-start', ml: 0, mr: 0, mb: 1 }}
                                        label={(
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="body1">
                                                    Publicera publikt
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Synligt för alla på marknaden
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                    <FormControlLabel
                                        value="channels"
                                        control={<Radio sx={{ mt: 0.25 }} />}
                                        sx={{ alignItems: 'flex-start', ml: 0, mr: 0 }}
                                        label={(
                                            <Box sx={{ py: 0.5 }}>
                                                <Typography variant="body1">
                                                    Publicera till valda kanaler
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    Grupper och/eller enskilda transportörer
                                                </Typography>
                                            </Box>
                                        )}
                                    />
                                </RadioGroup>
                            </FormControl>
                        </Grid>
                        {!entity.isPublic && (
                            <>
                                <Grid item xs={12}>
                                    <AutocompleteField<{ id: number, name: string, ownerTenantName?: string }>
                                        label="Grupper"
                                        helperText="Valfritt. Kan kombineras med transportörer."
                                        optionIdKey="id"
                                        fetchOptions={fetchGroups}
                                        options={groups.filter(
                                            (o: { isOwner: boolean; canPublish: boolean; }) => (o.isOwner || o.canPublish)
                                        )}
                                        getOptionName={(o) => o.name}
                                        getOptionDescription={(o) => o.ownerTenantName || ''}
                                        onChange={(g) => {
                                            const next = Array.isArray(g) ? g : [];
                                            onChange({
                                                groups: next,
                                                isPublic: false
                                            });
                                        }}
                                        fetchOnlyOnce
                                        displayOption={entity.groups || []}
                                        multiple
                                        required={
                                            !skipRequiredValidation
                                            && !(entity.recipients?.length)
                                        }
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <AutocompleteField<{ id: number, name: string, orgNumber?: string }>
                                        label="Transportörer"
                                        helperText="Valfritt. Kan kombineras med grupper."
                                        optionIdKey="id"
                                        fetchOptions={fetchCarriers}
                                        minQueryLength={3}
                                        getOptionName={(o) => o.name}
                                        getOptionDescription={(o) => o.orgNumber || ''}
                                        onChange={(r) => {
                                            const next = (Array.isArray(r) ? r : []).filter(
                                                (c: { id: number }) => c.id !== ownTenantIdRef.current
                                            );
                                            onChange({
                                                recipients: next,
                                                isPublic: false
                                            });
                                        }}
                                        displayOption={entity.recipients || []}
                                        multiple
                                        required={
                                            !skipRequiredValidation
                                            && !(entity.groups?.length)
                                        }
                                    />
                                </Grid>
                            </>
                        )}

                        <Grid item xs={12} lg={6}>
                            <NumberField
                                name="listPrice"
                                label="Riktpris [SEK]"
                                value={entity.listPrice}
                                onChange={onChange}
                                minValue={0}
                            />
                        </Grid>
                        <Grid item xs={12} lg={6}>
                            <DateTimeField
                                name="expiresAt"
                                label="Anmäl intresse senast"
                                value={entity.expiresAt}
                                onChange={onChange}
                                minDateTime={new Date()}
                                maxDateTime={
                                    entity.lastPickupDate || entity.firstPickupDate
                                        ? endOfDay(new Date(entity.lastPickupDate || entity.firstPickupDate))
                                        : undefined
                                }
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <TextField
                                name="publicDescription"
                                label="Publik beskrivning"
                                value={entity.publicDescription}
                                onChange={onChange}
                                helperText="Visas för alla som kan se uppdraget. Ex: Krav på biltyp, villkor etc."
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                name="privateDescription"
                                label="Privat beskrivning"
                                value={entity.privateDescription}
                                onChange={onChange}
                                helperText="Visas endast för den som tilldelas uppdraget. Ex: Leveransdetaljer som portkod etc."
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>
        </>
    );
}
